import { useEffect, useRef, useState } from "react";

import { debounce } from "utils/debounce";

export type ScanOptions = {
  onScan?: (scan: string) => void;
};

export function useScan(options?: ScanOptions) {
  const value = useRef<string>("");
  const [scannedValue, setScannedValue] = useState("");

  // Keep the latest callback in a ref so the keydown handler below is created only once.
  // Recreating it on every new onScan left the previous debounce timer pending, which then
  // fired mid-scan and split one scan into two partial ones.
  const onScanRef = useRef(options?.onScan);
  useEffect(() => {
    onScanRef.current = options?.onScan;
  }, [options?.onScan]);

  useEffect(() => {
    // When a scan occured the keydown is trigger very fast.
    // We are using the debounce function to reveal that behavior and know when the keydown sequence comes from a Zebra Scan
    const handleKeydown = debounce(() => {
      // if the function has been called inside and the value stored in ref is greater than 3 we know that is a scan
      const isScan = value.current.length > 3;
      if (isScan) {
        // store the scanned value in the state
        setScannedValue(value.current);
        // call the callback provided in options
        onScanRef.current?.(value.current);
      }
      // reset the value stored in ref
      value.current = "";
    }, 10);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key.length === 1) {
        // concat the letter returned by the event in a ref value
        value.current += event.key;
        handleKeydown();
      }
    };
    // This event will be trigger when a QR code is scanned by the ZEBRA. e.g. 'FOO' will trigger this event 3 times
    window.addEventListener("keydown", onKeyDown);
    // cleanup the event
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  return {
    scannedValue,
  };
}
